import { useState, useEffect } from "react";
import FilterComponent from "../../components/FilterComponent";
import CustomLoader from "../../common/CustomLoader/CustomLoader";
import reload from "../../assets/reload.svg";
import upArrow from "../../assets/uparrowFilter.svg";
import downArrow from "../../assets/downarrowFilter.svg";
import { Button } from "rsuite";
import Graph from "../../common/Graph";
import moment from "moment";
import { useSelector } from "react-redux";
import { IRoot } from "../../services/interface/IHome";
import { IDemandPayload } from "../../services/interface/IDemand";
import {
  useAccuracyDeepDiveApiDropDownFilterMutation,
  useAccuracyDeepDiveApiGraphDataMutation,
} from "../../services/redux/api/accuracyDeepDive";
interface DataPoint {
  dcAccuracy: number;
  dcBias?: number;
  TotalPounds?: number;
  category: string;
  subCategory: string;
  final_segment_erp: string;
}
const segmentColors = {
  A: "#046A38", 
  B: "#8ED07F", 
  C: "#CFE4A8", 
  D: "#666666", 
  E: "#93E9BE", 
};

const baseFilters = {
  dc: [],
  storeNumber: [],
  fcwNumber: [],
  itemId: [],
  filterItemId: [],
  ucode: [],
  productSegm: [],
  productDesc: [],
  category: [],
  subCategory: [],
  vendorName: [],
  modelType: [],
  adType: [],
};
interface SelectedPoint {
  subCategory: string;
  segment: string; // Final segment (final_segment_erp)
}
const AccuracyDeepDive = () => {
  const [isVisible, setIsVisible] = useState(true);
  const initialState:{[key:string]:string[]} = {
    dc: [],
    storeNumber: [],
    fcwNumber: ["3"],
    itemId: [],
    filterItemId: [],
    ucode: [],
    productSegm: [],
    productDesc: [],
    category: [],
    subCategory: [],
    vendorName: [],
    modelType: ["error based best model"],
    adType: [],
  }
  const [filters, setFilters] = useState<IDemandPayload>({
    dc: [],
    storeNumber: [],
    fcwNumber: [],
    itemId: [],
    filterItemId: [],
    ucode: [],
    productSegm: [],
    productDesc: [],
    category: [],
    subCategory: [],
    vendorName: [],
    modelType: [],
    adType: [],
  });

  const [selectedFilterData, setSelectedFilterData] = useState<{
    [key: string]: string[];
  }>(initialState);
  const [leftGraphData, setLeftGraphData] = useState<DataPoint[]>([]);
  const [rightGraphData, setRightGraphData] = useState<DataPoint[]>([]);
  const [currentFilters, setCurrentFilters] = useState(baseFilters);
  const [selectedPoint, setSelectedPoint] = useState<SelectedPoint | null>(null);

  const [fetchGraphData, { isLoading: isLoadingGraph }] =
    useAccuracyDeepDiveApiGraphDataMutation();

  const lastUpdated =
    useSelector((state: IRoot) => state.filter.lastUpdateDate) || "";

  const filterShowHandler = () => {
    setIsVisible((prev) => !prev);
  };

  const [triggerFilters, { isLoading }] =
    useAccuracyDeepDiveApiDropDownFilterMutation();

  const fetchFilters = async () => {
    try {
      // Create payload using selectedFilterData
      const payload: IDemandPayload = {
        dc: selectedFilterData.dc ?? [],
        storeNumber: selectedFilterData.storeNumber ?? [],
        fcwNumber: selectedFilterData.fcwNumber ?? [],
        itemId: selectedFilterData.itemId ?? [],
        filterItemId: selectedFilterData.filterItemId ?? [],
        ucode: selectedFilterData.ucode ?? [],
        productSegm: selectedFilterData.productSegm ?? [],
        productDesc: selectedFilterData.productDesc ?? [],
        category: selectedFilterData.category ?? [],
        subCategory: selectedFilterData.subCategory ?? [],
        vendorName: selectedFilterData.vendorName ?? [],
        modelType: selectedFilterData.modelType ?? [],
        adType: selectedFilterData.adType ?? [],
      };

      const response = await triggerFilters(payload).unwrap();
      setFilters(response?.data);
    } catch (error) {
      console.error("Error fetching filters:", error);
    }
  };

  useEffect(() => {
    fetchFilters();
  }, [selectedFilterData]);

  // Handle filter change
  const handleFilterChange = (filterKey: string, values: string[]) => {
    setSelectedFilterData((prevState) => ({
      ...prevState,
      [filterKey]: values,
    }));
  };

  const handleApply = () => {
    // Convert selectedFilterData to match baseFilters structure
    const filterPayload = {
      ...baseFilters,
      ...selectedFilterData,
    };
    fetchGraphDataWithFilters(filterPayload);
  };
  // Handle clear filters
  const handleClear = () => {
    setSelectedFilterData({});
    setSelectedPoint(null); 
    fetchFilters();
    fetchGraphDataWithFilters(baseFilters);
  };
  const fetchGraphDataWithFilters = async (filterPayload: any) => {
    try {
      const response = await fetchGraphData(filterPayload).unwrap();

      if (response?.data) {
        const { leftChart, rightChart } = response.data;

        // Always update both charts with the latest data
        if (leftChart && leftChart.length > 0) {
          setLeftGraphData(leftChart);
        }else{
          setLeftGraphData([]);
        }
        if (rightChart && rightChart.length > 0) {
          setRightGraphData(rightChart);
        }else{
          setRightGraphData([]);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchGraphDataWithFilters(baseFilters);
  }, []);

  // Effect to handle filter changes
  useEffect(() => {
    if (currentFilters !== baseFilters) {
      fetchGraphDataWithFilters(currentFilters);
    }
  }, [currentFilters]);

  const handleGraphClick = (clickedChart: "left" | "right", subCategory: string, segment: string) => {
    const hasSelectedFilters = Object.keys(selectedFilterData).length > 0;
    const combinedFilters = hasSelectedFilters
      ? { ...baseFilters, ...selectedFilterData }
      : { ...baseFilters };
  
    const newFilters = {
      ...combinedFilters,
      clickedChart,
      clickedSubCategory: subCategory,
      clickedSegment: segment, // Add segment to the filters
    };
  
    // Set the selected point in the parent
    setSelectedPoint({ subCategory, segment }); // Save object with subCategory and segment
  
    // Update filters and fetch new data
    setCurrentFilters(newFilters);
  };  
  

  return (
    <>
      <div className="relative">
        <div
          className={`flex flex-col gap-1 px-[2%] border-b-[1px] shadow ${
            !isVisible ? "pt-[8px] pb-[2px]" : "pt-[8px] pb-[15px]"
          }`}
        >
          <div className="flex justify-between items-center mb-1">
            <h3 className="page-title font-semibold">{"Demand Forecasting > Accuracy Deep Dive"}</h3>
            {
              <div className="flex gap-2">
                <img src={reload} alt="reload" className="w-[1vw]" />
                <p className="text-fs16 text-[#858585] font-semibold">
                  Last Updated :{" "}
                  <span>
                    {moment(lastUpdated?.slice(0, 10)).format("M/DD/YYYY")}
                  </span>
                </p>
              </div>
            }
          </div>

          {/* Filters Section */}
          {isVisible && (
            <div className="grid grid-cols-[1fr_auto] items-end gap-4 filter-div">
              <div className="grid grid-cols-12 gap-2 items-end">
              {filters && Object.entries(filters).map(([filterKey, filterData]) => {
              if (filterKey === "ucode" || !Array.isArray(filterData)) {
                return null;
              }

              // Filter out null values only for productSegm
              const filteredData = filterKey === "productSegm" 
                ? filterData.filter(filter => filter.filterVal !== null && filter.filterVal !== "null")
                : filterData;

              return (
                <FilterComponent
                  loading={isLoading}
                  key={filterKey}
                  filterKey={filterKey}
                  filterName={
                    filterKey === "viewBy"
                      ? "View By"
                      : filterKey === "adjustmentWeek"
                      ? "Select Week for Adjustment"
                      : filterKey === "dc"
                      ? "DC"
                      : filterKey === "storeNumber"
                      ? "Retail Store"
                      : filterKey === "productSegm"
                      ? "Segmentation"
                      : filterKey === "productDesc"
                      ? "Product Desc."
                      : filterKey === "itemId"
                      ? "Item ID"
                      : filterKey === "vendorName"
                      ? "Vendor"
                      : filterKey === "modelType"
                      ? "Model Type"
                      : filterKey === "adType"
                      ? "Ad Type"
                      : filterKey === "fcwNumber"
                      ? "Forecast Week"
                      : filterKey.charAt(0).toUpperCase() + filterKey.slice(1)
                  }
                  options={filteredData.map((filter: {
                    filterVal: string;
                    filterValue?: string;
                  }) => ({
                    label: filter.filterVal,
                    value: filter.filterValue || filter.filterVal,
                  }))}
                  labelKey="label"
                  valueKey="value"
                  value={selectedFilterData[filterKey] || []}
                  onFilterChange={handleFilterChange}
                />
              );
            })}

              </div>
              <div className="align-right flex gap-1">
                <Button
                  onClick={handleClear}
                  disabled={isLoading}
                  className="bg-[#949494] text-white px-[20px] h-[4vh] rounded font-semibold btn-text"
                >
                  Clear
                </Button>
                <Button
                  onClick={handleApply}
                  disabled={isLoading}
                  className="bg-deloitteGreen text-white px-[20px] h-[4vh] rounded font-semibold btn-text"
                >
                  Apply
                </Button>
              </div>
            </div>
          )}

          <div
            className="flex justify-center absolute left-2/4 top-full translate-x-[-50%] translate-y-[-50%] cursor-pointer z-[10]"
            onClick={filterShowHandler}
          >
            {isVisible && (
              <img className="w-[1.5vw]" src={upArrow} alt="upArrow" />
            )}
            {!isVisible && (
              <img className="w-[1.5vw]" src={downArrow} alt="downArrow" />
            )}
          </div>
        </div>
      </div>
      <div className="bg-[#F1F1F1] flex px-2 py-2"></div>
      {isLoadingGraph ? (
        <div>
          <CustomLoader open={isLoadingGraph} />
        </div>
      ) : (
        <div className={`bg-[#F1F1F1] flex px-[2%] pb-4 h-full gap-4 flex-grow overflow-hidden`}>
          <Graph
            title="Bias vs Accuracy : DC"
            customType="scatter"
            graphTypes={["scatter"]}
            dataKey={["dcBias"]}
            xAxisLabel="Accuracy"
            yAxisLabel="Bias"
            dataName={["Segment"]}
            fill={Object.values(segmentColors)}
            xAxisKey="dcAccuracy"
            data={leftGraphData}
            margin={{ top: 20, right: 20, bottom: 20, left: 0 }}
            isRequiredYAxisFormatter={true}
            isRequiredXAxisFormatter={true}
            selectedPoint={selectedPoint} 
            onClick={(data) => handleGraphClick("left", data.subCategory, data.final_segment_erp)}
            />

          <Graph
            title="Total Pounds vs Accuracy : DC"
            customType="scatter"
            graphTypes={["scatter"]}
            dataKey={["TotalPounds"]}
            dataName={["Segment"]}
            xAxisLabel="Accuracy"
            yAxisLabel="Total Pounds"
            fill={Object.values(segmentColors)}
            xAxisKey="dcAccuracy"
            data={rightGraphData}
            margin={{ top: 20, right: 20, bottom: 20, left: 0 }}
            isRequiredXAxisFormatter={true}
            selectedPoint={selectedPoint} 
            onClick={(data) => handleGraphClick("right", data.subCategory, data.final_segment_erp)}
            />
        </div>
      )}
    </>
  );
};

export default AccuracyDeepDive;
